import L from 'leaflet';
import type { Geometry, WorldRect, XZ } from './types';

/**
 * Per-map CRS: LatLngs ARE world metres (lat = Z north, lng = X east), and projected pixel (0,0)
 * sits at the world's top-left corner (minX, maxZ). Zoom z means 2^z screen px per metre.
 * `map.distance()` is Euclidean in CRS.Simple, so it returns metres directly.
 */
export function makeWorldCrs(rect: WorldRect): L.CRS {
  return L.extend({}, L.CRS.Simple, {
    transformation: new L.Transformation(1, -rect.minX, -1, rect.maxZ),
  }) as L.CRS;
}

export const toLatLng = (x: number, z: number): L.LatLng => L.latLng(z, x);
export const fromLatLng = (ll: L.LatLng): { x: number; z: number } => ({ x: ll.lng, z: ll.lat });
export const xzToLatLng = (p: XZ): L.LatLng => L.latLng(p[1], p[0]);
export const latLngToXZ = (ll: L.LatLng): XZ => [ll.lng, ll.lat];

export function worldRectToBounds(r: WorldRect): L.LatLngBounds {
  return L.latLngBounds(L.latLng(r.minZ, r.minX), L.latLng(r.maxZ, r.maxX));
}

/** Bounds of a geometry, for zoom-to. */
export function geometryBounds(g: Geometry): L.LatLngBounds | null {
  switch (g.type) {
    case 'point':
      return L.latLngBounds(toLatLng(g.x - 15, g.z - 15), toLatLng(g.x + 15, g.z + 15));
    case 'circle':
      return L.latLngBounds(toLatLng(g.x - g.r, g.z - g.r), toLatLng(g.x + g.r, g.z + g.r));
    case 'rect':
      return L.latLngBounds(toLatLng(g.x - g.w / 2, g.z - g.d / 2), toLatLng(g.x + g.w / 2, g.z + g.d / 2));
    case 'polygon':
    case 'polyline':
      return g.points.length ? L.latLngBounds(g.points.map(xzToLatLng)) : null;
    default:
      return null;
  }
}
